/**
 * tm/components/SendResultPanel.tsx — 동행상담 결과 보내기(담당 FP · 함께한 WM 님들) (2026-10-27 지도사
 *   「결과를 지정된 WM 님들에게 전송하는 버튼도 있어야 되는데, 동시에 보내거나」)
 *  받는 분을 골라(처음엔 보낼 수 있는 분 모두) 한 번에 · 본문 하나를 고치면 받는 분마다 「안녕하세요 ○○님」과 각자 포털 주소가 붙는다
 *  서버 services/consultSend(GET send-targets · POST send-result) · 결과는 받는 분마다 한 줄
 */
import { useEffect, useState } from 'react';
import { C, T, R } from './ui';
import * as api from '../api';
import type { SendLogRound, SendMorning, SendPending, SendTarget } from '../api';
import { SmsStateTag, smsWhen } from '../smsState';
import type { WM } from '../types';

interface Result { key: string; name: string; sent: boolean; error?: string }

export default function SendResultPanel({ consultId, onClose, onToast }: { consultId: string; onClose: () => void; onToast?: (msg: string, type?: 'success' | 'error') => void }) {
  const [list, setList] = useState<SendTarget[] | null>(null);
  const [body, setBody] = useState('');
  const [title, setTitle] = useState('');
  const [forward, setForward] = useState<{ report: boolean; collect: boolean; client: boolean } | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Result[] | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [waiting, setWaiting] = useState(0);   // 이 상담에서 정리 중인 녹음 수
  const [pending, setPending] = useState<SendPending | null>(null);   // 걸어 둔 「녹음 정리되면 보내기」
  // 근무 시간 밖 — WM 님 몫은 다음 근무일 오전 9시(2026-10-29 지도사 · 주말 · 공휴일 건너뜀) · night/due 는 서버 시각 · 공휴일 표로
  const [morning, setMorning] = useState<SendMorning | null>(null);
  const [night, setNight] = useState(false);
  const [morningDue, setMorningDue] = useState('');

  // 보낸 기록 — 받는 분마다 받음 · 보내는 중 · 못 받음(2026-10-29 지도사 「정상적으로 나갔는지 바로 보여 줘 · 답변을 다 안 주시니까」)
  const [log, setLog] = useState<SendLogRound[] | null>(null);
  const [logAll, setLogAll] = useState(false);
  const [pollUntil, setPollUntil] = useState(0);   // 보낸 직후 3분 — 통신사 결과가 올 때까지 6초마다 다시 봄
  const loadLog = () => api.getConsultSendLog(consultId).then((r) => { if (r.success && Array.isArray(r.data)) setLog(r.data); }).catch(() => undefined);
  useEffect(() => { void loadLog(); }, [consultId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const waitingLog = !!log && log.length > 0 && log[0].items.some((x) => x.state === 'wait');
  // 방금 보낸 차례 — 5분 안의 맨 위 차례만(이번에 아무에게도 못 나갔으면 지난 차례를 「방금」으로 보이지 않게)
  const fresh = log && log.length > 0 && Date.now() - new Date(log[0].at).getTime() < 5 * 60 * 1000 ? log[0] : null;
  useEffect(() => {
    if (!waitingLog || Date.now() > pollUntil) return undefined;
    const t = window.setTimeout(() => { void loadLog(); }, 6000);
    return () => window.clearTimeout(t);
  }, [log, pollUntil]);   // eslint-disable-line react-hooks/exhaustive-deps

  const [wms, setWms] = useState<WM[]>([]);
  const [adding, setAdding] = useState(false);
  /** 받는 분 다시 읽기 — keep = 고쳐 둔 본문 · 고른 분 유지(WM 을 더했을 때) */
  const loadTargets = (keep?: { body: string; picked: Set<string>; add?: string }) => api.getConsultSendTargets(consultId).then((r) => {
    if (!r.success || !r.data) { setLoadErr(r.error || '받는 분을 불러오지 못했어요'); return; }
    setList(r.data.recipients); setTitle(r.data.title || ''); setForward(r.data.forward || null); setWaiting(r.data.waiting || 0); setPending(r.data.pending || null);
    setMorning(r.data.morning || null); setNight(!!r.data.night); setMorningDue(r.data.morningDue || '');
    if (keep) { setBody(keep.body); setPicked(new Set(Array.from(keep.picked).concat(keep.add ? [keep.add] : []).filter((k) => r.data!.recipients.some((x) => x.key === k && !x.why)))); return; }
    setBody(r.data.body);
    setPicked(new Set(r.data.recipients.filter((x) => !x.why).map((x) => x.key)));
  }).catch(() => setLoadErr('받는 분을 불러오지 못했어요'));
  useEffect(() => { void loadTargets(); api.getWms().then((r) => { if (r.success && Array.isArray(r.data)) setWms(r.data); }).catch(() => undefined); }, [consultId]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** WM 더하기 — 이 상담의 함께한 WM(wm_ids)에 더해 저장 → 받는 분에 바로 나오고 체크됨(2026-10-29 지도사 「WM님에게 보내는 건 왜 안 나와」 — 상담 · 업체에 WM 이 없어서 줄이 아예 없었음) */
  const addWm = async (id: string) => {
    if (!id || !list) return;
    const now = list.filter((x) => x.role === 'WM').map((x) => x.key.slice(3));
    setAdding(true);
    try {
      const r = await api.updateConsultation(consultId, { wm_ids: Array.from(new Set([...now, id])) });
      if (!r.success) throw new Error(r.error || '저장하지 못했어요');
      await loadTargets({ body, picked, add: `wm:${id}` });
      if (onToast) onToast('WM님을 더했어요 · 이 상담의 함께한 WM 에도 저장', 'success');
    } catch (e) { if (onToast) onToast(e instanceof Error ? e.message : '저장하지 못했어요', 'error'); }
    setAdding(false);
  };

  const toggle = (k: string) => setPicked((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const send = async (later = false) => {
    if (!picked.size || busy) return;
    setBusy(true);
    try {
      const r = await api.sendConsultResult(consultId, body, Array.from(picked), later);
      if (r.success && r.data && r.data.pending) {   // 녹음 정리되면 보냄 — 걸어 둔 모습으로
        setPending({ state: 'pending', keys: Array.from(picked), at: new Date().toISOString(), results: [], error: '' });
        if (onToast) onToast(`녹음 정리가 끝나면 ${picked.size}분께 보내요`, 'success');
      } else if (r.success && r.data) {
        setResults(r.data.results);
        setPollUntil(Date.now() + 3 * 60 * 1000); void loadLog();
        const ok = r.data.results.filter((x) => x.sent).length;
        const m = r.data.morning;
        if (m) setMorning({ state: 'pending', keys: m.keys, due: m.due, results: [], error: '', sent_at: null });
        const later = m ? `WM님 ${m.keys.length}분께는 ${dueWord(m.due)}에 보내요` : '';
        if (onToast) onToast(ok ? [`${ok}분께 보냈어요`, later].filter(Boolean).join(' · ') : later || '보내지 못했어요', ok || later ? 'success' : 'error');
      } else if (onToast) onToast(r.error || '보내지 못했어요', 'error');
    } catch (e) { if (onToast) onToast(e instanceof Error ? e.message : '보내지 못했어요', 'error'); }
    setBusy(false);
  };

  const cancelLater = async () => {
    if (busy) return;
    setBusy(true);
    try { const r = await api.cancelConsultSend(consultId); if (r.success) { setPending(null); if (onToast) onToast('「정리되면 보내기」를 취소했어요', 'success'); } }
    catch { if (onToast) onToast('취소하지 못했어요', 'error'); }
    setBusy(false);
  };
  /** 아침 보내기 — 취소 · 지금 보내기 */
  const morningAct = async (kind: 'cancel' | 'now') => {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === 'cancel') { const r = await api.cancelConsultMorning(consultId); if (r.success) { setMorning(null); if (onToast) onToast('아침에 보낼 문자를 취소했어요', 'success'); } }
      else {
        const r = await api.sendConsultMorningNow(consultId);
        if (!r.success || !r.data) throw new Error(r.error || '보내지 못했어요');
        const ok = r.data.results.filter((x) => x.sent).length;
        setMorning((p) => (p ? { ...p, state: r.data!.state as SendMorning['state'], results: r.data!.results, sent_at: new Date().toISOString() } : p));
        setPollUntil(Date.now() + 3 * 60 * 1000); void loadLog();
        if (onToast) onToast(ok ? `WM님 ${ok}분께 지금 보냈어요` : '보내지 못했어요', ok ? 'success' : 'error');
      }
    } catch (e) { if (onToast) onToast(e instanceof Error ? e.message : '하지 못했어요', 'error'); }
    setBusy(false);
  };
  const nameOf = (k: string) => { const x = (list || []).find((t) => t.key === k); return x ? x.honor || `${x.name}님` : ''; };

  const btn = (primary: boolean, disabled = false): React.CSSProperties => ({
    flex: 1, height: 38, borderRadius: R.md, border: primary ? 'none' : `1px solid ${C.border}`, background: primary ? (disabled ? C.muted : C.accent) : '#fff',
    color: primary ? '#fff' : C.sub, fontSize: T.sm, fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
  });

  // 아침에 보낼 WM 님 몫 — 걸어 둔 것(받는 분 · 언제 · 지금 보내기 · 취소) · 보낸 뒤엔 결과 한 줄
  const morningBox = morning && (morning.state === 'pending' || morning.state === 'sending') ? (
    <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, background: '#fff', padding: '10px 12px', fontSize: T.sm, lineHeight: 1.6, color: C.text }}>
        <b>{morning.due ? dueWord(morning.due) : '아침'}에 WM님께 보내요</b>
        <div style={{ color: C.sub }}>{morning.keys.map(nameOf).filter(Boolean).join(' · ') || `${morning.keys.length}분`} · 근무 시간(평일 오전 9시~오후 6시)이 아닐 때 보낸 결과는 다음 근무일 오전 9시에 가요</div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" disabled={busy || morning.state === 'sending'} onClick={() => void morningAct('cancel')} style={btn(false, busy)}>보내기 취소</button>
        <button type="button" disabled={busy || morning.state === 'sending'} onClick={() => void morningAct('now')} style={btn(false, busy)}>지금 보내기</button>
      </div>
    </div>
  ) : morning && morning.sent_at && Date.now() - new Date(morning.sent_at).getTime() < 3 * 24 * 3600 * 1000 ? (
    <div style={{ fontSize: T.sm, color: morning.state === 'failed' ? C.red : C.green, padding: '0 0 6px', lineHeight: 1.5 }}>
      {smsWhen(morning.sent_at)} WM님께 {morning.state === 'failed' ? '못 보냈어요' : '보냈어요'} — {morning.results.map((x) => `${x.name}${x.sent ? '' : '(못 보냄)'}`).join(' · ') || morning.error}
    </div>
  ) : null;
  const pickedWm = Array.from(picked).filter((k) => k.startsWith('wm:')).length;

  return (
    <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, background: '#F8FAFC' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: T.sm, fontWeight: 700, color: C.text }}>결과 보내기</div>
        <div style={{ fontSize: T.xs, color: C.muted }}>문자 · 받는 분마다 이름과 각자 포털 주소가 달라요</div>
      </div>

      {loadErr && <div style={{ fontSize: T.sm, color: C.red, padding: '6px 0' }}>{loadErr}</div>}
      {!list && !loadErr && <div style={{ fontSize: T.sm, color: C.muted, padding: '6px 0' }}>받는 분을 불러오는 중…</div>}

      {/* 걸어 둔 「녹음 정리되면 보내기」 — 기다리는 중이면 받는 분 · 취소 · 지금 보내기 · 이미 보냈으면 결과(2026-10-29) */}
      {list && !results && pending && (pending.state === 'pending' || pending.state === 'sending') ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, background: '#fff', padding: '10px 12px', fontSize: T.sm, lineHeight: 1.6, color: C.text }}>
            <b>녹음 정리가 끝나면 보내요</b>
            <div style={{ color: C.sub }}>{pending.keys.map(nameOf).filter(Boolean).join(' · ') || `${pending.keys.length}분`}{waiting ? ` · 정리 중인 녹음 ${waiting}개` : ''}</div>
            {pending.keys.some((k) => k.startsWith('wm:')) && <div style={{ color: C.muted, fontSize: T.xs }}>정리가 근무 시간(평일 오전 9시~오후 6시) 밖에 끝나면 WM님께는 다음 근무일 오전 9시에 가요(팀장님께는 바로)</div>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" disabled={busy || pending.state === 'sending'} onClick={() => void cancelLater()} style={btn(false, busy)}>보내기 취소</button>
            <button type="button" disabled={busy || pending.state === 'sending'} onClick={() => { setPicked(new Set(pending.keys)); setPending(null); }} style={btn(false, busy)}>지금 보내기로 바꾸기</button>
          </div>
        </div>
      ) : list && (results ? (
        <div style={{ display: 'grid', gap: 4 }}>
          {morningBox}
          {/* 방금 보낸 차례 — 통신사 결과가 오면 「받음」으로 바뀜(6초마다 · 3분) · 연락처 없음처럼 아예 안 나간 분은 그대로 「못 보냄」 */}
          {fresh ? <LogRound round={fresh} /> : results.map((x) => (
            <div key={x.key} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, fontSize: T.sm }}>
              <SmsStateTag state={x.sent ? 'wait' : 'rejected'} note={x.error} />
              <span style={{ color: C.text, fontWeight: 600 }}>{x.name}</span>
              {x.error && <span style={{ color: C.muted }}>{x.error}</span>}
            </div>
          ))}
          {results.filter((x) => !x.sent && !(fresh && fresh.items.some((y) => y.name.startsWith(`${x.name} `)))).map((x) => <div key={x.key} style={{ fontSize: T.xs, color: C.red }}>{x.name} — {x.error || '못 보냈어요'}</div>)}
          {waitingLog && Date.now() < pollUntil && <div style={{ fontSize: T.xs, color: C.muted }}>통신사 결과를 기다리는 중이에요 · 보통 1분 안에 「받음」으로 바뀌어요</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}><button type="button" onClick={onClose} style={btn(false)}>닫기</button></div>
        </div>
      ) : (
        <>
          {morningBox}
          {pending && ['sent', 'partial', 'failed'].includes(pending.state) && (
            <div style={{ fontSize: T.sm, color: pending.state === 'failed' ? C.red : C.green, padding: '0 0 6px', lineHeight: 1.5 }}>
              녹음 정리 뒤 {pending.state === 'failed' ? '못 보냈어요' : '보냈어요'} — {pending.results.map((x) => `${x.name}${x.sent ? '' : '(못 보냄)'}`).join(' · ') || pending.error}
            </div>
          )}
          {log && log.length > 0 && (
            <div style={{ marginBottom: 10, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: R.md, background: '#fff' }}>
              {(logAll ? log : log.slice(0, 1)).map((rd, i) => <LogRound key={rd.at + i} round={rd} />)}
              {log.length > 1 && <button type="button" onClick={() => setLogAll((v) => !v)} style={{ marginTop: 4, border: 'none', background: 'none', padding: 0, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>{logAll ? '접기' : `전에 보낸 것 ${log.length - 1}번 더 보기`}</button>}
            </div>
          )}
          {list.length === 0 && <div style={{ fontSize: T.sm, color: C.muted, padding: '6px 0' }}>이 상담에 담당 FP · WM 이 없어요</div>}
          <div style={{ display: 'grid', gap: 2, marginBottom: 10 }}>
            {list.map((x) => {
              const off = !!x.why;
              return (
                <label key={x.key} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 4px', borderRadius: R.sm, cursor: off ? 'default' : 'pointer', opacity: off ? 0.55 : 1 }}>
                  <input type="checkbox" disabled={off} checked={picked.has(x.key)} onChange={() => toggle(x.key)} style={{ width: 16, height: 16, margin: 0 }} />
                  <span style={{ width: 28, fontSize: T.xs, fontWeight: 700, color: C.sub, flexShrink: 0 }}>{x.role}</span>
                  <span style={{ fontSize: T.sm, fontWeight: 600, color: C.text }}>{x.name}님</span>
                  <span style={{ fontSize: T.xs, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{off ? x.why : x.phone}</span>
                </label>
              );
            })}
            {/* WM 더하기 — 같은 줄 모양(체크 자리에 +) · 고르면 저장하고 받는 분에 체크돼 나옴 */}
            {(() => {
              const have = new Set(list.filter((x) => x.role === 'WM').map((x) => x.key.slice(3)));
              const left = wms.filter((w) => !have.has(w.id));
              if (!left.length) return null;
              return (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 34, padding: '0 4px' }}>
                  <span style={{ width: 16, textAlign: 'center', fontWeight: 700, color: C.accent }}>+</span>
                  <span style={{ width: 28, fontSize: T.xs, fontWeight: 700, color: C.sub, flexShrink: 0 }}>WM</span>
                  <select value="" disabled={adding} onChange={(e) => void addWm(e.target.value)} aria-label="WM 더하기"
                    style={{ height: 30, minWidth: 0, flex: '0 1 240px', border: `1px solid ${C.border}`, borderRadius: R.sm, padding: '0 8px', fontSize: T.sm, color: C.sub, background: '#fff', fontFamily: 'inherit' }}>
                    <option value="">{adding ? '더하는 중…' : have.size ? 'WM 더하기' : '이 상담에 WM 이 없어요 — 골라서 더하기'}</option>
                    {left.map((w) => <option key={w.id} value={w.id}>{w.name}{w.company ? ` · ${w.company}` : ''}</option>)}
                  </select>
                </label>
              );
            })()}
          </div>
          {/* 받는 모습 그대로 — 제목 · 인사는 정해진 꼴, 가운데 본문만 고친다(2026-10-28 지도사 「[임지도사] (주)상승테크 동행상담 결과 안내 / 안녕하세요 WM님 / ~~~~ / URL」) */}
          {(() => {
            const first = list.find((x) => picked.has(x.key)) || list[0];
            const line: React.CSSProperties = { fontSize: T.sm, lineHeight: 1.6, color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'keep-all' };
            return (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, background: '#fff', padding: '10px 12px', display: 'grid', gap: 8 }}>
                <div style={{ ...line, fontWeight: 700 }}>{title}</div>
                <div style={line}>안녕하세요 {first ? first.honor : '받는 분'}</div>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} aria-label="보낼 글(가운데 본문)"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: R.sm, border: `1.5px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.6, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', background: '#F8FAFC' }} />
                {/* 대표님께 전달할 것 · 받는 분 전용 포털을 나눠 보낸다(2026-10-28 지도사 「FP님 페이지는 고객에게 보내면 안 되고 …」 · services/customerLinks) */}
                {/* 대표님께 전달할 것 — 팀장님(FP)께만(WM 은 전달하지 않음 · 2026-10-29) */}
                {forward && first && first.role === 'FP' && (forward.report || forward.collect || forward.client) && (
                  <div style={{ ...line, color: C.muted }}>
                    <div style={{ color: C.text, fontWeight: 600 }}>▶ 대표님께 그대로 전달해 주세요</div>
                    {[forward.report ? '· 상담 리포트 (주소)' : '', forward.collect ? '· 필요서류 제출 (주소)' : '', forward.client ? '· 대표님 진행 안내 (주소)' : ''].filter(Boolean).join('\n')}
                  </div>
                )}
                <div style={{ ...line, color: C.muted }}>
                  <div style={{ color: C.text, fontWeight: 600 }}>▶ {first && first.role === 'WM' ? 'WM님' : '팀장님'} 전용 포털</div>
                  (받는 분마다 각자 주소)
                </div>
              </div>
            );
          })()}
          {/* 녹음이 아직 정리 중이면 — 「정리되면 보내기」가 먼저(진하게) · 지금 보내기도 그대로(2026-10-29 지도사) */}
          {/* 저녁 6시 뒤 — WM 님은 다음 날 오전 9시(2026-10-29 지도사) */}
          {night && pickedWm > 0 && <div style={{ fontSize: T.xs, color: C.sub, marginTop: 8 }}>근무 시간이 아니라 WM님 {pickedWm}분께는 {morningDue ? dueWord(morningDue) : '내일 오전 9:00'}에 보내요{picked.has('fp') ? ' · 팀장님께는 바로' : ''}</div>}
          {/* 녹음이 아직 대화 글로 정리 중이면 — 보내기를 누르면 정리가 끝날 때 저절로 문자(2026-10-29 지도사 「파일 올리고 결과 보내기 누르면 텍스트화 다 되면 문자로」) */}
          {waiting > 0 && <div style={{ fontSize: T.xs, color: C.sub, marginTop: 8 }}>녹음 {waiting}개를 대화 글로 정리하고 있어요 · 보내기를 누르면 정리가 끝날 때 대화 글까지 담겨 저절로 문자가 가요</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={onClose} style={btn(false)}>취소</button>
            <button type="button" disabled={busy || !picked.size} onClick={() => void send(waiting > 0)} style={{ ...btn(true, busy || !picked.size), flex: 2 }}>
              {busy ? '보내는 중…' : !picked.size ? '받는 분을 골라 주세요' : waiting > 0 ? `정리되면 ${picked.size}분께 보내기` : `${picked.size}분께 보내기`}
            </button>
          </div>
        </>
      ))}
    </div>
  );
}

/** 「내일 오전 9:00」 · 오늘이면 「오늘 오전 9:00」 · 그 밖은 「10/30 오전 9:00」 (한국 시각) */
function dueWord(iso: string): string {
  const day = (t: number) => new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
  const d = day(new Date(iso).getTime()), today = day(Date.now()), tomorrow = day(Date.now() + 24 * 3600 * 1000);
  const w = smsWhen(iso), time = w.slice(w.indexOf(' ') + 1);
  return d === today ? `오늘 ${time}` : d === tomorrow ? `내일 ${time}` : w;
}

/** 보낸 한 차례 — 「10/29 오후 2:05 보냄」 + 받는 분마다 상태 */
function LogRound({ round }: { round: SendLogRound }) {
  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 2 }}>{smsWhen(round.at)} 보냄</div>
      {round.items.map((x, i) => (
        <div key={x.name + i} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 30, fontSize: T.sm }}>
          <SmsStateTag state={x.state} note={x.note} />
          <span style={{ color: C.text, fontWeight: 600 }}>{x.name}</span>
          {x.note && <span style={{ color: C.muted, fontSize: T.xs }}>{x.note}</span>}
        </div>
      ))}
    </div>
  );
}
